import React, { useState } from 'react';
import { X } from 'lucide-react';
import litigationEventService from '../../services/litigationEventService';
import {
  EVENT_TYPE_GROUPS, EVENT_TYPE_LABELS, EVENT_ORIGIN_LABELS, SERVICE_METHOD_LABELS,
  HEARING_DATE_TYPES, defaultOriginFor,
} from '../../utils/litigationLabels';

// Now, as "YYYY-MM-DDTHH:mm" for a datetime-local default.
function nowLocal() {
  const n = new Date();
  const off = n.getTimezoneOffset() * 60000;
  return new Date(n - off).toISOString().slice(0, 16);
}
// A datetime-local value -> ISO (UTC). Empty -> null.
function dtToISO(v) { return v ? new Date(v).toISOString() : null; }
// A date value -> ISO at local noon (keeps the calendar date stable across time zones).
function dateToISO(v) { return v ? new Date(`${v}T12:00:00`).toISOString() : null; }
// ISO -> "YYYY-MM-DDTHH:mm" (datetime-local) in local time.
function isoToLocalDT(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d - off).toISOString().slice(0, 16);
}
// ISO -> "YYYY-MM-DD" in local time.
function isoToDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d - off).toISOString().slice(0, 10);
}

const inputCls = 'w-full bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg px-3 py-2 ' +
  'text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500';
const labelCls = 'block text-sm text-virgo-teal-300 mb-1';

const LitigationEventForm = ({ caseId, event, onClose, onCreated }) => {
  const isEdit = !!event;
  const [eventType, setEventType] = useState(event?.event_type || 'complaint_filed');
  const [eventOrigin, setEventOrigin] = useState(event?.event_origin || defaultOriginFor('complaint_filed'));
  const [occurredAt, setOccurredAt] = useState(event ? isoToLocalDT(event.occurred_at) : nowLocal());
  const [serviceDate, setServiceDate] = useState(event ? isoToDate(event.service_date) : '');
  const [serviceMethod, setServiceMethod] = useState(event?.service_method || 'email');
  const [hearingDate, setHearingDate] = useState(event ? isoToDate(event.hearing_date) : '');
  const [description, setDescription] = useState(event?.description || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const showHearing = HEARING_DATE_TYPES.has(eventType);

  const onTypeChange = (v) => {
    setEventType(v);
    setEventOrigin(defaultOriginFor(v));   // smart default; user can override
    if (!HEARING_DATE_TYPES.has(v)) setHearingDate('');
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        event_type: eventType,
        event_origin: eventOrigin,
        occurred_at: dtToISO(occurredAt),
        service_date: dateToISO(serviceDate),
        service_method: serviceMethod || null,
        hearing_date: showHearing ? dateToISO(hearingDate) : null,
        description: description.trim() || null,
      };
      let saved;
      if (isEdit) {
        saved = await litigationEventService.updateEvent(event.id, payload);
      } else {
        saved = await litigationEventService.createEvent({ case_id: caseId, ...payload });
      }
      onCreated(saved);   // parent refreshes timeline + deadlines
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to log the event. Please check the fields and try again.');
      setSubmitting(false);   // keep the form open + populated on error
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">{isEdit ? 'Edit Litigation Event' : 'Log a Litigation Event'}</h3>
          <button onClick={onClose} className="text-virgo-teal-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {error && (
          <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-3 mb-4">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className={labelCls}>Event type</label>
            <select value={eventType} onChange={(e) => onTypeChange(e.target.value)} className={inputCls}>
              {EVENT_TYPE_GROUPS.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.types.map((t) => <option key={t} value={t}>{EVENT_TYPE_LABELS[t] || t}</option>)}
                </optgroup>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Origin</label>
              <select value={eventOrigin} onChange={(e) => setEventOrigin(e.target.value)} className={inputCls}>
                {Object.entries(EVENT_ORIGIN_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Occurred at</label>
              <input type="datetime-local" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} className={inputCls} required />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Service date <span className="text-virgo-teal-500">(if different)</span></label>
              <input type="date" value={serviceDate} onChange={(e) => setServiceDate(e.target.value)} className={inputCls} />
              <p className="text-xs text-virgo-teal-500 mt-1">Deadlines run from service; leave blank to use occurred-at.</p>
            </div>
            <div>
              <label className={labelCls}>Service method</label>
              <select value={serviceMethod} onChange={(e) => setServiceMethod(e.target.value)} className={inputCls}>
                {Object.entries(SERVICE_METHOD_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>

          {showHearing && (
            <div>
              <label className={labelCls}>Hearing date <span className="text-virgo-teal-500">(deadlines compute backward from this)</span></label>
              <input type="date" value={hearingDate} onChange={(e) => setHearingDate(e.target.value)} className={inputCls} />
            </div>
          )}

          <div>
            <label className={labelCls}>Description <span className="text-virgo-teal-500">(optional)</span></label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className={inputCls} placeholder="Notes about this event…" />
          </div>

          <div className="flex space-x-3 pt-2">
            <button type="button" onClick={onClose} disabled={submitting}
              className="flex-1 px-4 py-2 border border-virgo-teal-700/50 rounded-lg text-virgo-teal-300 hover:bg-virgo-navy-600 disabled:opacity-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="flex-1 px-4 py-2 rounded-lg text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 transition-all">
              {submitting ? 'Saving…' : (isEdit ? 'Save Changes' : 'Log Event')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LitigationEventForm;
